/**
 * Parses uploaded files — spreadsheets (SheetJS), images (base64), text files, PDFs.
 * Returns structured ParsedFileData ready for the fileStore.
 */
import * as XLSX from 'xlsx';
import type { ParsedFileData, ParsedSheet } from './file-store';

/* ─── Extension lists ─────────────────────────────── */

const SPREADSHEET_EXTENSIONS = ['.xlsx', '.xls', '.csv', '.tsv'] as const;

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp'] as const;

const TEXT_EXTENSIONS = [
  '.txt', '.md', '.json', '.xml', '.html', '.css',
  '.js', '.ts', '.py', '.yaml', '.yml',
] as const;

const DOCUMENT_EXTENSIONS = ['.pdf'] as const;

/** Every extension we accept. */
export const ACCEPTED_EXTENSIONS = [
  ...SPREADSHEET_EXTENSIONS,
  ...IMAGE_EXTENSIONS,
  ...TEXT_EXTENSIONS,
  ...DOCUMENT_EXTENSIONS,
] as const;

/** MIME types that map to accepted extensions. */
export const ACCEPTED_MIME_TYPES = [
  // Spreadsheets
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel',                                          // .xls
  'text/csv',                                                          // .csv
  'text/tab-separated-values',                                         // .tsv
  // Images
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  // Text
  'text/plain', 'text/markdown', 'application/json', 'text/xml',
  'text/html', 'text/css', 'text/javascript', 'application/javascript',
  // Documents
  'application/pdf',
] as const;

/** Accept string for file input elements. */
export const FILE_ACCEPT = ACCEPTED_EXTENSIONS.join(',');

/* ─── Helpers ─────────────────────────────────────── */

function extOf(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  return idx >= 0 ? fileName.slice(idx).toLowerCase() : '';
}

function isSpreadsheet(name: string): boolean {
  return (SPREADSHEET_EXTENSIONS as readonly string[]).includes(extOf(name));
}

function isImage(name: string): boolean {
  return (IMAGE_EXTENSIONS as readonly string[]).includes(extOf(name));
}

function isText(name: string): boolean {
  return (TEXT_EXTENSIONS as readonly string[]).includes(extOf(name));
}

function isDocument(name: string): boolean {
  return (DOCUMENT_EXTENSIONS as readonly string[]).includes(extOf(name));
}

/**
 * Check if a file name has a supported extension.
 */
export function isSupportedFile(fileName: string): boolean {
  return (ACCEPTED_EXTENSIONS as readonly string[]).includes(extOf(fileName));
}

/* ─── Parsers ─────────────────────────────────────── */

/**
 * Parse a File object into structured data.
 * Routes to the appropriate parser based on extension / MIME type.
 */
export async function parseFile(file: File): Promise<ParsedFileData> {
  const name = file.name;

  if (isImage(name) || file.type.startsWith('image/')) {
    return parseImageFile(file);
  }

  if (isText(name)) {
    return parseTextFile(file);
  }

  if (isDocument(name)) {
    // PDFs: send as base64 so vision models can process them
    return parseDocumentFile(file);
  }

  // Default: spreadsheet (original path)
  return parseSpreadsheetFile(file);
}

/**
 * Read an image file as a base64 data URI.
 */
async function parseImageFile(file: File): Promise<ParsedFileData> {
  const base64 = await readAsDataURL(file);
  return {
    fileId: crypto.randomUUID(),
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || 'image/png',
    type: 'image',
    sheets: [],
    totalCells: 0,
    base64Data: base64,
    rawFile: file,
    parsedAt: Date.now(),
  };
}

/**
 * Read a text file as UTF-8 string.
 */
async function parseTextFile(file: File): Promise<ParsedFileData> {
  const text = await file.text();
  return {
    fileId: crypto.randomUUID(),
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || 'text/plain',
    type: 'text',
    sheets: [],
    totalCells: 0,
    textContent: text,
    rawFile: file,
    parsedAt: Date.now(),
  };
}

/**
 * Read a document (PDF) as base64 data URI for vision models.
 */
async function parseDocumentFile(file: File): Promise<ParsedFileData> {
  const base64 = await readAsDataURL(file);
  return {
    fileId: crypto.randomUUID(),
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || 'application/pdf',
    type: 'image', // treated as image for vision model consumption
    sheets: [],
    totalCells: 0,
    base64Data: base64,
    rawFile: file,
    parsedAt: Date.now(),
  };
}

/**
 * Parse a spreadsheet file with SheetJS (original logic).
 */
async function parseSpreadsheetFile(file: File): Promise<ParsedFileData> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });

  const sheets: ParsedSheet[] = [];
  let totalCells = 0;

  for (const sheetName of workbook.SheetNames) {
    const worksheet = workbook.Sheets[sheetName];

    const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(
      worksheet,
      { defval: '' },
    );

    const headers =
      jsonData.length > 0
        ? Object.keys(jsonData[0])
        : getHeadersFromRange(worksheet);

    const totalRows = jsonData.length;
    const totalColumns = headers.length;
    totalCells += totalRows * totalColumns;

    sheets.push({
      name: sheetName,
      headers,
      rows: jsonData,
      totalRows,
      totalColumns,
    });
  }

  return {
    fileId: crypto.randomUUID(),
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || 'application/octet-stream',
    type: 'spreadsheet',
    sheets,
    totalCells,
    rawFile: file,
    parsedAt: Date.now(),
  };
}

/* ─── Utility ─────────────────────────────────────── */

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

/**
 * Extract headers from a worksheet range when there's no data rows.
 */
function getHeadersFromRange(worksheet: XLSX.WorkSheet): string[] {
  const ref = worksheet['!ref'];
  if (!ref) return [];

  const range = XLSX.utils.decode_range(ref);
  const headers: string[] = [];

  for (let c = range.s.c; c <= range.e.c; c++) {
    const cellAddress = XLSX.utils.encode_cell({ r: range.s.r, c });
    const cell = worksheet[cellAddress];
    headers.push(cell ? String(cell.v) : `Column${c + 1}`);
  }

  return headers;
}
